import React, { useEffect, useState } from 'react';
import {
  collectionGroup,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getAuth } from 'firebase/auth';

const STATUS_OPTIONS = [
  { value: 'abierto', label: 'Abierto' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'en_espera_cliente', label: 'En espera del cliente' },
  { value: 'resuelto', label: 'Resuelto' },
  { value: 'cerrado', label: 'Cerrado' },
];

const PRIORITY_LABELS = {
  baja: 'Baja',
  media: 'Media',
  alta: 'Alta',
  critica: 'Crítica',
};

const PRIORITY_STYLES = {
  baja: 'bg-gray-100 text-gray-700',
  media: 'bg-blue-100 text-blue-700',
  alta: 'bg-amber-100 text-amber-700',
  critica: 'bg-red-100 text-red-700',
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = value.toDate?.() || new Date(value);
  if (!(date instanceof Date) || isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-PR', { month: 'short', day: 'numeric' });
};

const ServiceDesk = () => {
  const auth = getAuth();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState('');
  const [statusFilter, setStatusFilter] = useState('abierto');
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const snap = await getDocs(collectionGroup(db, 'serviceTickets'));
        const list = snap.docs.map((docSnap) => {
          const pathSegments = docSnap.ref.path.split('/');
          const clientIdIndex = pathSegments.indexOf('clients') + 1;
          const clientId = pathSegments[clientIdIndex];
          return {
            id: docSnap.id,
            clientId,
            ...docSnap.data(),
          };
        });
        setTickets(list);
      } catch (err) {
        console.error('❌ Error cargando tickets globales:', err);
      }
    };
    fetchTickets();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredTickets(tickets);
    } else {
      setFilteredTickets(tickets.filter((t) => (t.status || 'abierto') === statusFilter));
    }
  }, [tickets, statusFilter]);

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingTimeline(true);
    try {
      const q = query(
        collection(db, 'clients', ticket.clientId, 'serviceTickets', ticket.id, 'timeline'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snap = await getDocs(q);
      setTimeline(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('❌ Error cargando timeline del ticket:', err);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const addTimelineEntry = async () => {
    if (!selectedTicket || !note.trim()) return;
    try {
      await addDoc(collection(db, 'clients', selectedTicket.clientId, 'serviceTickets', selectedTicket.id, 'timeline'), {
        summary: note.trim(),
        channel: 'admin',
        entityType: 'serviceTicket',
        ticketTitle: selectedTicket.title,
        clientName: selectedTicket.clientName,
        createdBy: currentUser?.email || 'admin',
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'clients', selectedTicket.clientId, 'serviceTickets', selectedTicket.id), {
        updatedAt: serverTimestamp(),
      });
      setNote('');
      openTicket(selectedTicket);
    } catch (err) {
      console.error('❌ Error añadiendo nota de servicio:', err);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedTicket) return;
    try {
      await updateDoc(doc(db, 'clients', selectedTicket.clientId, 'serviceTickets', selectedTicket.id), {
        status,
        updatedAt: serverTimestamp(),
      });
      setTickets((prev) => prev.map((t) => (t.id === selectedTicket.id && t.clientId === selectedTicket.clientId ? { ...t, status } : t)));
      setSelectedTicket((prev) => prev && { ...prev, status });
    } catch (err) {
      console.error('❌ Error actualizando estado del ticket:', err);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Centro de servicios</h1>
          <p className="text-sm text-gray-500">Gestiona las solicitudes que han abierto los clientes.</p>
        </div>
        <select
          className="border rounded px-3 py-2 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos los estados</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-lg shadow divide-y divide-gray-100">
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-lg font-semibold text-gray-800">Tickets</h2>
            <span className="text-sm text-gray-500">{filteredTickets.length} resultados</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {filteredTickets.map((ticket) => (
              <article
                key={`${ticket.clientId}-${ticket.id}`}
                className={`px-4 py-3 border-b cursor-pointer hover:bg-gray-50 ${
                  selectedTicket && ticket.id === selectedTicket.id && ticket.clientId === selectedTicket.clientId
                    ? 'bg-orange-50'
                    : 'bg-white'
                }`}
                onClick={() => openTicket(ticket)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">{ticket.title}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${PRIORITY_STYLES[ticket.priority] || 'bg-gray-100'}`}>
                    {PRIORITY_LABELS[ticket.priority] || ticket.priority}
                  </span>
                </div>
                <p className="text-xs text-gray-500">Cliente: {ticket.clientName || ticket.clientId}</p>
                <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                  <span>{STATUS_OPTIONS.find((s) => s.value === ticket.status)?.label || ticket.status}</span>
                  <span>{formatDate(ticket.updatedAt || ticket.createdAt)}</span>
                </div>
              </article>
            ))}
            {filteredTickets.length === 0 && (
              <p className="px-4 py-6 text-sm text-gray-500">No hay tickets con este filtro.</p>
            )}
          </div>
        </section>

        <section className="bg-white rounded-lg shadow p-6">
          {selectedTicket ? (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase text-gray-500">Ticket</p>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedTicket.title}</h2>
                  <p className="text-sm text-gray-500">
                    {selectedTicket.clientName || selectedTicket.clientEmail}
                  </p>
                </div>
                <select
                  className="border rounded px-3 py-2 text-sm"
                  value={selectedTicket.status}
                  onChange={(e) => updateStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Timeline</h3>
                {loadingTimeline ? (
                  <p className="text-sm text-gray-500">Cargando…</p>
                ) : timeline.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin actualizaciones.</p>
                ) : (
                  <ul className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    {timeline.map((entry) => (
                      <li key={entry.id} className="border rounded px-3 py-2 text-sm">
                        <p className="text-gray-800">{entry.summary}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                          <span>{entry.createdBy || entry.channel}</span>
                          <span>
                            {entry.createdAt?.toDate
                              ? entry.createdAt
                                  .toDate()
                                  .toLocaleString('es-PR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    month: 'short',
                                    day: 'numeric',
                                  })
                              : ''}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-4 space-y-2">
                <textarea
                  rows={3}
                  className="border rounded w-full px-3 py-2 text-sm"
                  placeholder="Añade una actualización para el cliente"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button
                  type="button"
                  onClick={addTimelineEntry}
                  className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium"
                >
                  Enviar actualización
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Selecciona un ticket para ver los detalles.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default ServiceDesk;
